import { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Blend,
  RoastBatch,
  SensorCondition,
  SensorReading,
  OvenTriggerRule,
  OvenAutomationRulesConfig,
  listBlends,
  createBlend,
  updateBlend,
  updateBatch,
  deleteBlend,
  getDailyPlan,
  createBatch,
  deleteBatch,
  cancelBatch,
  reorderBatches,
  getLatestSensorReadings,
  getOvenAutomationRules,
  setOvenAutomationRules,
} from '../api/client';

const ROAST_LEVELS = [
  { label: 'Coffee 1',  color: '#748060' },
  { label: 'Coffee 2',  color: '#917D60' },
  { label: 'Coffee 3',  color: '#846A52' },
  { label: 'Coffee 4',  color: '#745642' },
  { label: 'Coffee 5',  color: '#5E4231' },
  { label: 'Coffee 6',  color: '#483227' },
  { label: 'Coffee 7',  color: '#332621' },
  { label: 'Coffee 8',  color: '#221D1B' },
  { label: 'Coffee 9',  color: '#1B1817' },
  { label: 'Coffee 10', color: '#141414' },
];

function hexToRgbTuple(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

function scoreToRgb(score: number): [number, number, number] {
  const clamped = Math.max(0, Math.min(100, score));
  const t = (clamped / 100) * (ROAST_LEVELS.length - 1);
  const lo = Math.floor(t);
  const hi = Math.min(ROAST_LEVELS.length - 1, lo + 1);
  const f = t - lo;
  const [r0, g0, b0] = hexToRgbTuple(ROAST_LEVELS[lo].color);
  const [r1, g1, b1] = hexToRgbTuple(ROAST_LEVELS[hi].color);
  return [
    Math.round(r0 + (r1 - r0) * f),
    Math.round(g0 + (g1 - g0) * f),
    Math.round(b0 + (b1 - b0) * f),
  ];
}

function rgbToScore(rgb: [number, number, number]): number {
  let best = 0;
  let bestDist = Infinity;
  ROAST_LEVELS.forEach((level, i) => {
    const [r, g, b] = hexToRgbTuple(level.color);
    const dist = (rgb[0] - r) ** 2 + (rgb[1] - g) ** 2 + (rgb[2] - b) ** 2;
    if (dist < bestDist) { bestDist = dist; best = i; }
  });
  return Math.round((best / (ROAST_LEVELS.length - 1)) * 100);
}

function roastLevelLabel(score: number): { label: string; color: string } {
  const idx = Math.round((Math.max(0, Math.min(100, score)) / 100) * (ROAST_LEVELS.length - 1));
  return ROAST_LEVELS[idx];
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m} דק׳ ${s.toString().padStart(2, '0')} שנ׳`;
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

// ── Blend Card (HTML5 native drag for blend→plan) ────────────────────

function BlendCard({ blend, onClick }: { blend: Blend; onClick: () => void }) {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('application/blend-id', blend.id);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={onClick}
      className="bg-amber-50 hover:bg-amber-100 active:bg-amber-200 border-2 border-amber-200 hover:border-amber-400 rounded-xl p-5 text-center transition-all touch-manipulation cursor-grab active:cursor-grabbing select-none"
    >
      <div className="flex items-center justify-center gap-2 mb-1">
        <div className="font-bold text-xl text-gray-800">{blend.name}</div>
        {blend.target_color_rgb && (
          <div
            className="w-4 h-4 rounded-full border border-gray-300 shrink-0"
            style={{ backgroundColor: `rgb(${blend.target_color_rgb.join(',')})` }}
            title={`צבע יעד: rgb(${blend.target_color_rgb.join(', ')})`}
          />
        )}
      </div>
      <div className="text-sm text-gray-500">{formatTime(blend.required_roast_time_seconds)}</div>
      {blend.target_roast_score != null && (
        <div className="text-xs text-amber-700 font-semibold mt-0.5">יעד: {Math.round(blend.target_roast_score)}/100</div>
      )}
      {(blend.start_condition || blend.end_condition) && (
        <div className="mt-1 flex justify-center gap-1 flex-wrap">
          {blend.start_condition && (
            <span className="text-[10px] bg-green-100 text-green-700 rounded px-1.5 py-0.5" title={`התחלה: ${blend.start_condition.sensor_device_id} ${blend.start_condition.sensor_register} ${blend.start_condition.operator} ${blend.start_condition.threshold}`}>▶ אוטו</span>
          )}
          {blend.end_condition && (
            <span className="text-[10px] bg-red-100 text-red-700 rounded px-1.5 py-0.5" title={`סיום: ${blend.end_condition.sensor_device_id} ${blend.end_condition.sensor_register} ${blend.end_condition.operator} ${blend.end_condition.threshold}`}>■ אוטו</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sortable batch item (dnd-kit for reordering) ─────────────────────

function SortableBatchItem({ batch, onDelete }: { batch: RoastBatch; onDelete: (id: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: batch.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm"
    >
      <div className="flex items-center gap-4">
        <button {...attributes} {...listeners} className="cursor-grab text-gray-400 hover:text-gray-600 text-2xl p-2">
          ⠿
        </button>
        <div>
          <div className="font-bold text-xl text-gray-800" dir="auto">{batch.blend_name}</div>
          <div className="text-lg text-gray-500" dir="rtl">
            <bdi>{batch.quantity}</bdi> {batch.unit === 'kg' ? 'ק״ג' : 'אצוות'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="px-3 py-1 text-sm rounded-full font-bold bg-blue-100 text-blue-700">
          מתוכנן
        </span>
        <button onClick={() => onDelete(batch.id)} className="text-red-400 hover:text-red-600 text-2xl p-2">
          ✕
        </button>
      </div>
    </div>
  );
}

// ── Static batch item (non-draggable, for in-progress batches) ───────

function StaticBatchItem({ batch, onCancel }: { batch: RoastBatch; onCancel: (id: string) => void }) {
  return (
    <div className="flex items-center justify-between bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="text-yellow-400 text-2xl p-2">🔥</div>
        <div>
          <div className="font-bold text-xl text-gray-800" dir="auto">{batch.blend_name}</div>
          <div className="text-lg text-gray-500" dir="rtl">
            <bdi>{batch.quantity}</bdi> {batch.unit === 'kg' ? 'ק״ג' : 'אצוות'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="px-3 py-1 text-sm rounded-full font-bold bg-yellow-100 text-yellow-700">
          בביצוע
        </span>
        <button
          onClick={() => onCancel(batch.id)}
          className="text-red-400 hover:text-red-600 text-xl p-2"
          title="ביטול"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// ── Blend Selector Banner (for in-list unknown blend replacement) ─────

function BlendSelectorBanner({ batch, onBlendSet }: { batch: RoastBatch; onBlendSet: (b: RoastBatch) => void }) {
  const [allBlends, setAllBlends] = useState<{ id: string; name: string }[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listBlends().then(data => setAllBlends(data.map(b => ({ id: b.id, name: b.name })))).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!selectedId) return;
    const blend = allBlends.find(b => b.id === selectedId);
    if (!blend) return;
    setSaving(true);
    try {
      const updated = await updateBatch(batch.id, { blend_id: blend.id, blend_name: blend.name });
      onBlendSet(updated);
    } catch (err) {
      console.error('Failed to set blend:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap mt-1">
      <select
        value={selectedId}
        onChange={e => setSelectedId(e.target.value)}
        className="flex-1 px-2 py-1 border border-orange-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none bg-white min-w-0"
      >
        <option value="">-- בחר תערובת --</option>
        {allBlends.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <button
        onClick={handleSave}
        disabled={!selectedId || saving}
        className="px-3 py-1 bg-orange-600 text-white rounded-lg text-sm font-bold hover:bg-orange-700 disabled:opacity-50 shrink-0"
      >
        {saving ? '...' : 'שמור'}
      </button>
    </div>
  );
}

// ── Completed batch item (for done batches in the left panel) ────────

function isUnknown(batch: RoastBatch): boolean {
  return batch.blend_id === 'unknown' || batch.blend_name === 'Unknown';
}

function CompletedBatchItem({ batch, onBatchUpdated }: { batch: RoastBatch; onBatchUpdated?: (b: RoastBatch) => void }) {
  const unknown = isUnknown(batch);

  return (
    <div className={`flex flex-col gap-2 rounded-xl p-3 border ${unknown ? 'bg-orange-50 border-orange-300' : 'bg-green-50 border-green-200'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`text-lg ${unknown ? 'text-orange-500' : 'text-green-500'}`}>{unknown ? '⚠️' : '✓'}</div>
          <div>
            <div className={`font-bold text-lg ${unknown ? 'text-orange-700' : 'text-gray-700'}`} dir="auto">
              {batch.blend_name}
              {unknown && <span className="ml-1 text-xs font-normal">• תערובת לא ידועה</span>}
            </div>
            <div className="text-base text-gray-500" dir="rtl">
              <bdi>{batch.quantity}</bdi> {batch.unit === 'kg' ? 'ק״ג' : 'אצוות'}
            </div>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs rounded-full font-bold ${unknown ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
          {unknown ? 'לא ידוע' : 'הושלם'}
        </span>
      </div>
      {unknown && onBatchUpdated && (
        <BlendSelectorBanner batch={batch} onBlendSet={onBatchUpdated} />
      )}
    </div>
  );
}

// ── Add to Plan Modal ────────────────────────────────────────────────

function AddToPlanModal({
  blend,
  onClose,
  onAdd,
}: {
  blend: Blend;
  onClose: () => void;
  onAdd: (blendId: string, quantity: number, unit: string) => void;
}) {
  const [quantity, setQuantity] = useState<number>(1);
  const [unit, setUnit] = useState('batches');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (quantity <= 0) return;
    onAdd(blend.id, quantity, unit);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-2xl font-bold text-gray-800 mb-2 text-center">{blend.name}</h3>
        <p className="text-gray-500 text-center mb-6">
          זמן קלייה: {formatTime(blend.required_roast_time_seconds)}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-3">כמות</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="w-16 h-16 flex items-center justify-center text-3xl font-bold rounded-2xl border-2 border-gray-300 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation select-none shrink-0"
              >
                −
              </button>
              <input
                type="number"
                step={1}
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                className="min-w-0 flex-1 px-4 py-4 text-3xl font-bold text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                style={{ MozAppearance: 'textfield' } as React.CSSProperties}
              />
              <button
                type="button"
                onClick={() => setQuantity((q) => q + 1)}
                className="w-16 h-16 flex items-center justify-center text-3xl font-bold rounded-2xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 transition-colors touch-manipulation select-none shrink-0"
              >
                +
              </button>
            </div>
          </div>

          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">יחידה</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setUnit('kg')}
                className={`py-4 text-xl font-bold rounded-xl border-2 transition-colors ${
                  unit === 'kg'
                    ? 'bg-amber-700 text-white border-amber-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400'
                }`}
              >
                ק״ג
              </button>
              <button
                type="button"
                onClick={() => setUnit('batches')}
                className={`py-4 text-xl font-bold rounded-xl border-2 transition-colors ${
                  unit === 'batches'
                    ? 'bg-amber-700 text-white border-amber-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400'
                }`}
              >
                אצוות
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="py-4 text-xl font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              ביטול
            </button>
            <button
              type="submit"
              disabled={quantity <= 0}
              className="py-4 text-xl font-bold text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:bg-gray-300 transition-colors"
            >
              הוסף לתכנית
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Blend Management Modal ───────────────────────────────────────────

const OPERATORS = [
  { value: '>', label: '> גדול מ' },
  { value: '<', label: '< קטן מ' },
  { value: '>=', label: '>= גדול או שווה ל' },
  { value: '<=', label: '<= קטן או שווה ל' },
  { value: '==', label: '== שווה ל' },
  { value: '!=', label: '!= שונה מ' },
];

function ConditionEditor({
  label,
  condition,
  onChange,
  sensors,
}: {
  label: string;
  condition: SensorCondition | null;
  onChange: (c: SensorCondition | null) => void;
  sensors: SensorReading[];
}) {
  const devices = Array.from(new Set(sensors.map(s => s.device_id))).sort();
  const registers = condition
    ? sensors.filter(s => s.device_id === condition.sensor_device_id)
    : [];

  const enabled = condition !== null;

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id={`${label}-toggle`}
          checked={enabled}
          onChange={e =>
            onChange(
              e.target.checked
                ? { sensor_device_id: devices[0] ?? '', sensor_register: '', operator: '>', threshold: 0 }
                : null,
            )
          }
          className="w-5 h-5 accent-amber-600"
        />
        <label htmlFor={`${label}-toggle`} className="text-base font-medium text-gray-700">{label}</label>
      </div>

      {enabled && condition && (
        <div className="grid grid-cols-1 gap-3 pl-8">
          {/* Device selector */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">מכשיר (Device)</label>
            {sensors.length > 0 ? (
              <select
                value={condition.sensor_device_id}
                onChange={e =>
                  onChange({ ...condition, sensor_device_id: e.target.value, sensor_register: '' })
                }
                className="w-full px-3 py-2 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              >
                <option value="">בחר מכשיר</option>
                {devices.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            ) : (
              <input
                type="text"
                value={condition.sensor_device_id}
                onChange={e => onChange({ ...condition, sensor_device_id: e.target.value })}
                placeholder="device_id"
                className="w-full px-3 py-2 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              />
            )}
          </div>

          {/* Register selector */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">רגיסטר (Register)</label>
            {registers.length > 0 ? (
              <select
                value={condition.sensor_register}
                onChange={e => onChange({ ...condition, sensor_register: e.target.value })}
                className="w-full px-3 py-2 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              >
                <option value="">בחר רגיסטר</option>
                {registers.map(r => (
                  <option key={r.register_address} value={r.register_address}>
                    {r.register_name && r.register_name !== r.register_address
                      ? `${r.register_name} (${r.register_address})`
                      : r.register_address}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={condition.sensor_register}
                onChange={e => onChange({ ...condition, sensor_register: e.target.value })}
                placeholder="register_address"
                className="w-full px-3 py-2 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              />
            )}
          </div>

          {/* Operator + Threshold */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="block text-sm text-gray-500 mb-1">תנאי</label>
              <select
                value={condition.operator}
                onChange={e => onChange({ ...condition, operator: e.target.value })}
                className="w-full px-3 py-2 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              >
                {OPERATORS.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm text-gray-500 mb-1">ערך סף</label>
              <input
                type="number"
                value={condition.threshold}
                onChange={e => onChange({ ...condition, threshold: Number(e.target.value) })}
                className="w-full px-3 py-2 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BlendModal({
  blend,
  onClose,
  onSave,
}: {
  blend: Blend | null;
  onClose: () => void;
  onSave: (
    name: string,
    seconds: number,
    targetColorRgb: [number, number, number] | null,
    targetRoastScore: number | null,
    startCondition: SensorCondition | null,
    endCondition: SensorCondition | null,
  ) => void;
}) {
  const [name, setName] = useState(blend?.name || '');
  const [minutes, setMinutes] = useState(blend ? Math.floor(blend.required_roast_time_seconds / 60) : 12);
  const [seconds, setSeconds] = useState(blend ? blend.required_roast_time_seconds % 60 : 0);
  const [colorScore, setColorScore] = useState<number>(
    blend?.target_roast_score != null
      ? blend.target_roast_score
      : blend?.target_color_rgb
        ? rgbToScore(blend.target_color_rgb)
        : 50,
  );
  const [hasTargetColor, setHasTargetColor] = useState(!!blend?.target_color_rgb);
  const [targetRoastScore, setTargetRoastScore] = useState<number | null>(
    blend?.target_roast_score ?? null,
  );
  const [startCondition, setStartCondition] = useState<SensorCondition | null>(
    blend?.start_condition ?? null,
  );
  const [endCondition, setEndCondition] = useState<SensorCondition | null>(
    blend?.end_condition ?? null,
  );
  const [sensors, setSensors] = useState<SensorReading[]>([]);

  useEffect(() => {
    getLatestSensorReadings().then(setSensors).catch(() => {});
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const total = minutes * 60 + seconds;
    if (!name || total <= 0) return;
    onSave(name, total, hasTargetColor ? scoreToRgb(colorScore) : null, targetRoastScore, startCondition, endCondition);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          {blend ? 'עריכת תערובת' : 'תערובת חדשה'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">שם</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              className="w-full px-4 py-3 text-xl border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">זמן קלייה</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setMinutes((m) => Math.max(0, m - 1))}
                className="w-14 h-14 flex items-center justify-center text-2xl font-bold rounded-xl border-2 border-gray-300 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation select-none shrink-0">−</button>
              <input
                type="number"
                min={0}
                value={minutes}
                onChange={(e) => setMinutes(Math.max(0, Number(e.target.value)))}
                className="w-20 px-2 py-3 text-xl text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              />
              <button type="button" onClick={() => setMinutes((m) => m + 1)}
                className="w-14 h-14 flex items-center justify-center text-2xl font-bold rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 transition-colors touch-manipulation select-none shrink-0">+</button>
              <span className="text-lg text-gray-500 mx-1">דק׳</span>
              <button type="button" onClick={() => setSeconds((s) => Math.max(0, s - 5))}
                className="w-14 h-14 flex items-center justify-center text-2xl font-bold rounded-xl border-2 border-gray-300 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation select-none shrink-0">−</button>
              <input
                type="number"
                min={0}
                max={59}
                value={seconds}
                onChange={(e) => setSeconds(Math.min(59, Math.max(0, Number(e.target.value))))}
                className="w-20 px-2 py-3 text-xl text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              />
              <button type="button" onClick={() => setSeconds((s) => Math.min(59, s + 5))}
                className="w-14 h-14 flex items-center justify-center text-2xl font-bold rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 transition-colors touch-manipulation select-none shrink-0">+</button>
              <span className="text-lg text-gray-500 mx-1">שנ׳</span>
            </div>
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">צבע יעד</label>
            <div className="flex items-center gap-3 mb-2">
              <input
                type="checkbox"
                checked={hasTargetColor}
                onChange={(e) => setHasTargetColor(e.target.checked)}
                className="w-5 h-5 accent-amber-600"
                id="target-color-toggle"
              />
              <label htmlFor="target-color-toggle" className="text-base text-gray-600">הגדר צבע יעד</label>
            </div>
            {hasTargetColor && (
              <div className="mt-1">
                <div className="relative h-10 rounded-lg overflow-hidden border border-gray-300"
                  style={{ background: `linear-gradient(to right, ${ROAST_LEVELS.map((l) => l.color).join(', ')})` }}
                >
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={colorScore}
                    onChange={(e) => setColorScore(Number(e.target.value))}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div
                    className="absolute top-0 bottom-0 w-1 bg-white/80 shadow-lg pointer-events-none rounded-full"
                    style={{ left: `calc(${colorScore}% - 2px)` }}
                  />
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <div
                    className="w-8 h-8 rounded-lg border-2 border-gray-300 shrink-0"
                    style={{ backgroundColor: `rgb(${scoreToRgb(colorScore).join(',')})` }}
                  />
                  <span className="text-base font-semibold text-gray-700">
                    {roastLevelLabel(colorScore).label}
                  </span>
                  <span className="text-sm text-gray-500">({colorScore}/100)</span>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">יעד סקאלת קלייה (0–100)</label>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={targetRoastScore !== null}
                onChange={(e) => setTargetRoastScore(e.target.checked ? 50 : null)}
                className="w-5 h-5 accent-amber-600"
                id="target-score-toggle"
              />
              <label htmlFor="target-score-toggle" className="text-base text-gray-600">הגדר יעד קלייה</label>
              {targetRoastScore !== null && (
                <>
                  <button type="button" onClick={() => setTargetRoastScore((s) => Math.max(10, (s ?? 50) - 5))}
                    className="w-10 h-10 flex items-center justify-center text-xl font-bold rounded-xl border-2 border-gray-300 bg-gray-100 hover:bg-gray-200 transition-colors">−</button>
                  <input
                    type="number"
                    min={10}
                    max={100}
                    value={targetRoastScore}
                    onChange={(e) => setTargetRoastScore(Math.min(100, Math.max(10, Number(e.target.value))))}
                    className="w-20 px-2 py-2 text-xl text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  />
                  <button type="button" onClick={() => setTargetRoastScore((s) => Math.min(100, (s ?? 50) + 5))}
                    className="w-10 h-10 flex items-center justify-center text-xl font-bold rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 transition-colors">+</button>
                </>
              )}
            </div>
          </div>

          {/* ── Automation Conditions ── */}
          <div className="space-y-3">
            <label className="block text-lg font-medium text-gray-700">תנאי אוטומציה</label>
            <ConditionEditor
              label="תנאי התחלת קלייה"
              condition={startCondition}
              onChange={setStartCondition}
              sensors={sensors}
            />
            <ConditionEditor
              label="תנאי סיום קלייה"
              condition={endCondition}
              onChange={setEndCondition}
              sensors={sensors}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button type="button" onClick={onClose} className="py-3 text-lg font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              ביטול
            </button>
            <button type="submit" className="py-3 text-lg font-bold text-white bg-amber-700 rounded-xl hover:bg-amber-800 transition-colors">
              {blend ? 'עדכן' : 'הוסף'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Manager Screen ──────────────────────────────────────────────

const MODBUS_OPERATORS = [
  { value: 'greater_than', label: '> גדול מ' },
  { value: 'greater_than_or_equal', label: '>= גדול או שווה ל' },
  { value: 'less_than', label: '< קטן מ' },
  { value: 'less_than_or_equal', label: '<= קטן או שווה ל' },
  { value: 'equal', label: '== שווה ל' },
  { value: 'not_equal', label: '!= שונה מ' },
];

const MACHINE_IDS = ['1', '2'];

// ── Oven Automation Section ──────────────────────────────────────────

function OvenTriggerRuleEditor({
  rule,
  onChange,
  registerNames,
}: {
  rule: OvenTriggerRule;
  onChange: (r: OvenTriggerRule) => void;
  registerNames: string[];
}) {
  const isStart = rule.event_type === 'roast_start';
  return (
    <div className={`rounded-xl border-2 p-4 space-y-3 ${isStart ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
      <div className="flex items-center gap-3">
        <span className={`font-bold text-sm ${isStart ? 'text-green-700' : 'text-red-700'}`}>
          {isStart ? '▶ התחלת קלייה' : '■ סיום קלייה'}
        </span>
        <label className="flex items-center gap-1 text-xs text-gray-500 ml-auto">
          <input
            type="checkbox"
            checked={rule.enabled}
            onChange={e => onChange({ ...rule, enabled: e.target.checked })}
            className="accent-gray-500"
          />
          פעיל
        </label>
      </div>

      {/* Register name */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">שם רגיסטר (Sensor)</label>
        {registerNames.length > 0 ? (
          <select
            value={rule.register_name}
            onChange={e => onChange({ ...rule, register_name: e.target.value })}
            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
          >
            <option value="">בחר רגיסטר</option>
            {registerNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        ) : (
          <input
            type="text"
            value={rule.register_name}
            onChange={e => onChange({ ...rule, register_name: e.target.value })}
            placeholder="Mbus_DrumTemp@Prog16"
            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
          />
        )}
      </div>

      {/* Operator + threshold */}
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">תנאי</label>
          <select
            value={rule.operator}
            onChange={e => onChange({ ...rule, operator: e.target.value })}
            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
          >
            {MODBUS_OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
          </select>
        </div>
        <div className="w-28">
          <label className="block text-xs text-gray-500 mb-1">ערך</label>
          <input
            type="number"
            value={rule.threshold}
            onChange={e => onChange({ ...rule, threshold: Number(e.target.value) })}
            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>
        <div className="w-20">
          <label className="block text-xs text-gray-500 mb-1">דגימות</label>
          <input
            type="number"
            min={1}
            max={10}
            value={rule.consecutive_samples_required}
            onChange={e => onChange({ ...rule, consecutive_samples_required: Math.max(1, Number(e.target.value)) })}
            title="מספר דגימות רצופות לאישור לפני שיגור אירוע"
            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>
      </div>
      <p className="text-xs text-gray-400">דגימות: מינימום {rule.consecutive_samples_required} דגימות רצופות שמקיימות את התנאי לפני שיגור האירוע (מניעת ספייקים)</p>
    </div>
  );
}

function OvenAutomationSection({ sensorReadings }: { sensorReadings: SensorReading[] }) {
  const [expanded, setExpanded] = useState(false);
  const [configs, setConfigs] = useState<Record<string, OvenAutomationRulesConfig>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});

  const registerNames = Array.from(
    new Set(sensorReadings.map(r => r.register_name).filter(Boolean))
  ).sort();

  useEffect(() => {
    if (!expanded) return;
    MACHINE_IDS.forEach(async (id) => {
      try {
        const cfg = await getOvenAutomationRules(id);
        setConfigs(prev => ({ ...prev, [id]: cfg }));
      } catch {
        // backend might return 404 for unconfigured ovens — start with empty
      }
    });
  }, [expanded]);

  const handleRuleChange = (machineId: string, updated: OvenTriggerRule[]) => {
    setConfigs(prev => ({
      ...prev,
      [machineId]: { ...(prev[machineId] ?? { machine_id: machineId, rules: [] }), rules: updated },
    }));
    setSaved(prev => ({ ...prev, [machineId]: false }));
  };

  const handleSave = async (machineId: string) => {
    const cfg = configs[machineId];
    if (!cfg) return;
    setSaving(prev => ({ ...prev, [machineId]: true }));
    try {
      await setOvenAutomationRules(machineId, cfg.rules);
      setSaved(prev => ({ ...prev, [machineId]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [machineId]: false })), 2000);
    } catch (err) {
      console.error('Failed to save automation rules:', err);
    } finally {
      setSaving(prev => ({ ...prev, [machineId]: false }));
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg shrink-0">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
      >
        <h2 className="text-xl font-bold text-gray-700">⚙ טריגרים לתנורים (Modbus)</h2>
        <span className="text-gray-400 text-lg">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {MACHINE_IDS.map(machineId => {
            const cfg = configs[machineId];
            const startRule = cfg?.rules.find(r => r.event_type === 'roast_start') ?? {
              event_type: 'roast_start' as const,
              register_name: 'Mbus_DrumTemp@Prog16',
              operator: 'greater_than',
              threshold: 100,
              consecutive_samples_required: 3,
              enabled: true,
            };
            const endRule = cfg?.rules.find(r => r.event_type === 'roast_end') ?? {
              event_type: 'roast_end' as const,
              register_name: 'Mbus_DrumTemp@Prog16',
              operator: 'less_than',
              threshold: 80,
              consecutive_samples_required: 3,
              enabled: true,
            };

            const updateRule = (updated: OvenTriggerRule) => {
              const current = configs[machineId]?.rules ?? [startRule, endRule];
              const next = current.some(r => r.event_type === updated.event_type)
                ? current.map(r => r.event_type === updated.event_type ? updated : r)
                : [...current, updated];
              handleRuleChange(machineId, next);
            };

            return (
              <div key={machineId} className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-600 border-b pb-2">תנור {machineId}</h3>
                <OvenTriggerRuleEditor
                  rule={startRule}
                  onChange={updateRule}
                  registerNames={registerNames}
                />
                <OvenTriggerRuleEditor
                  rule={endRule}
                  onChange={updateRule}
                  registerNames={registerNames}
                />
                <button
                  onClick={() => handleSave(machineId)}
                  disabled={saving[machineId]}
                  className={`w-full py-2 rounded-xl font-bold text-sm transition-colors ${
                    saved[machineId]
                      ? 'bg-green-500 text-white'
                      : 'bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50'
                  }`}
                >
                  {saving[machineId] ? 'שומר...' : saved[machineId] ? '✓ נשמר' : 'שמור טריגרים לתנור ' + machineId}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Manager() {
  const [blends, setBlends] = useState<Blend[]>([]);
  const [batches, setBatches] = useState<RoastBatch[]>([]);
  const [sensorReadings, setSensorReadings] = useState<SensorReading[]>([]);
  const [planDate, setPlanDate] = useState(todayStr());
  const [selectedBlend, setSelectedBlend] = useState<Blend | null>(null);
  const [showBlendForm, setShowBlendForm] = useState(false);
  const [editingBlend, setEditingBlend] = useState<Blend | null>(null);
  const [showBlendManagement, setShowBlendManagement] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const loadBlends = useCallback(async () => {
    try {
      const data = await listBlends();
      setBlends(data);
    } catch (err) { console.error('Failed to load blends:', err); }
  }, []);

  const loadPlan = useCallback(async () => {
    try {
      const data = await getDailyPlan(planDate);
      setBatches(data.batches);
    } catch (err) { console.error('Failed to load plan:', err); }
  }, [planDate]);

  useEffect(() => { loadBlends(); }, [loadBlends]);
  useEffect(() => { loadPlan(); }, [loadPlan]);
  useEffect(() => {
    getLatestSensorReadings().then(setSensorReadings).catch(() => {});
  }, []);

  // ── Blend CRUD ──
  const handleSaveBlend = async (name: string, totalSeconds: number, targetColorRgb: [number, number, number] | null, targetRoastScore: number | null, startCondition: SensorCondition | null, endCondition: SensorCondition | null) => {
    try {
      if (editingBlend) {
        await updateBlend(editingBlend.id, { name, required_roast_time_seconds: totalSeconds, target_color_rgb: targetColorRgb, target_roast_score: targetRoastScore, start_condition: startCondition, end_condition: endCondition });
        setEditingBlend(null);
      } else {
        await createBlend(name, totalSeconds, targetColorRgb, targetRoastScore, startCondition, endCondition);
      }
      setShowBlendForm(false);
      loadBlends();
    } catch (err) { console.error('Failed to save blend:', err); }
  };

  const handleDeleteBlend = async (id: string) => {
    if (!confirm('למחוק תערובת זו?')) return;
    try {
      await deleteBlend(id);
      loadBlends();
    } catch (err) { console.error('Failed to delete blend:', err); }
  };

  // ── Batch CRUD ──
  const handleAddBatch = async (blendId: string, quantity: number, unit: string) => {
    try {
      await createBatch(blendId, quantity, unit, planDate);
      setSelectedBlend(null);
      loadPlan();
    } catch (err) { console.error('Failed to create batch:', err); }
  };

  const handleDeleteBatch = async (id: string) => {
    try {
      await deleteBatch(id);
      loadPlan();
    } catch (err) { console.error('Failed to delete batch:', err); }
  };

  const handleCancelBatch = async (id: string) => {
    try {
      await cancelBatch(id);
      loadPlan();
    } catch (err) { console.error('Failed to cancel batch:', err); }
  };

  // ── Derived batch lists ──
  const inProgressBatches = batches.filter((b) => b.status === 'in-progress');
  const plannedBatches = batches.filter((b) => b.status === 'planned');
  const doneBatches = batches.filter((b) => b.status === 'done');

  // ── dnd-kit: reorder planned batches only ──
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = plannedBatches.findIndex((b) => b.id === active.id);
    const newIndex = plannedBatches.findIndex((b) => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedPlanned = arrayMove(plannedBatches, oldIndex, newIndex);
    // Rebuild full list: in-progress first, then reordered planned, then done
    const reordered = [...inProgressBatches, ...reorderedPlanned, ...doneBatches];
    setBatches(reordered);

    try {
      await reorderBatches(reordered.map((b) => b.id));
    } catch (err) { console.error('Failed to reorder:', err); }
  };

  // ── HTML5 native drag: blend → plan drop zone ──
  const handlePlanDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('application/blend-id')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    }
  };

  const handlePlanDragLeave = (e: React.DragEvent) => {
    // Only clear if leaving the drop zone itself (not entering a child)
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  };

  const handlePlanDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const blendId = e.dataTransfer.getData('application/blend-id');
    if (!blendId) return;
    const blend = blends.find((b) => b.id === blendId);
    if (blend) {
      setSelectedBlend(blend);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      <div className="flex items-center justify-between shrink-0">
        <h1 className="text-3xl font-extrabold text-gray-800">מנהל — תכנון ייצור</h1>
        <button
          onClick={() => setShowBlendManagement(!showBlendManagement)}
          className="px-5 py-3 bg-gray-200 text-gray-700 rounded-xl text-lg font-bold hover:bg-gray-300 transition-colors"
        >
          {showBlendManagement ? 'סגור ניהול תערובות' : '⚙ ניהול תערובות'}
        </button>
      </div>

      {/* ── Blend Management (collapsible) ── */}
      {showBlendManagement && (        <div className="bg-white rounded-2xl shadow-lg p-6 shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-700">ניהול תערובות</h2>
            <button
              onClick={() => { setEditingBlend(null); setShowBlendForm(true); }}
              className="px-5 py-3 bg-amber-700 text-white rounded-xl text-lg font-bold hover:bg-amber-800 transition-colors"
            >
              + תערובת חדשה
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {blends.map((blend) => (
              <div key={blend.id} className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-lg text-gray-700">{blend.name}</div>
                  <div className="text-sm text-gray-500">{formatTime(blend.required_roast_time_seconds)}</div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setEditingBlend(blend); setShowBlendForm(true); }}
                    className="text-amber-600 hover:text-amber-800 text-lg font-bold p-2"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteBlend(blend.id)}
                    className="text-red-500 hover:text-red-700 text-lg font-bold p-2"
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Oven Automation Triggers (collapsible) ── */}
      <OvenAutomationSection sensorReadings={sensorReadings} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
        {/* ── Blend Selection Grid ── */}
        <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col overflow-hidden">
          <h2 className="text-2xl font-bold text-gray-700 mb-2 shrink-0">בחר תערובת להוספה</h2>
          <p className="text-gray-400 text-base mb-4 shrink-0">לחץ או גרור תערובת אל התכנית</p>
          <div className="grid grid-cols-2 gap-3 overflow-y-auto flex-1 min-h-0 content-start">
            {blends.map((blend) => (
              <BlendCard
                key={blend.id}
                blend={blend}
                onClick={() => setSelectedBlend(blend)}
              />
            ))}
          </div>
          {blends.length === 0 && (
            <p className="text-gray-400 text-lg text-center py-8">אין תערובות. הוסף דרך ניהול תערובות.</p>
          )}
        </div>

        {/* ── Daily Plan (HTML5 drop zone + dnd-kit sortable reorder) ── */}
        <div
          className={`bg-white rounded-2xl shadow-lg p-6 transition-all flex flex-col overflow-hidden ${
            isDragOver ? 'ring-4 ring-amber-400 bg-amber-50' : ''
          }`}
          onDragOver={handlePlanDragOver}
          onDragLeave={handlePlanDragLeave}
          onDrop={handlePlanDrop}
        >
          <div className="flex items-center justify-between mb-4 shrink-0">
            <h2 className="text-2xl font-bold text-gray-700">תכנית יומית</h2>
            <input
              type="date"
              value={planDate}
              onChange={(e) => setPlanDate(e.target.value)}
              className="px-4 py-2 border-2 border-gray-300 rounded-xl text-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
            />
          </div>

          {isDragOver && (
            <div className="text-center py-3 mb-3 bg-amber-100 rounded-xl border-2 border-dashed border-amber-400 shrink-0">
              <p className="text-amber-700 text-xl font-bold">⬇ שחרר כאן להוספה לתכנית</p>
            </div>
          )}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className="space-y-3 overflow-y-auto flex-1 min-h-0">
              {/* ── In-progress batches (pinned, not draggable) ── */}
              {inProgressBatches.map((batch) => (
                <StaticBatchItem key={batch.id} batch={batch} onCancel={handleCancelBatch} />
              ))}

              {/* ── Planned batches (sortable) ── */}
              <SortableContext items={plannedBatches.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                {plannedBatches.map((batch) => (
                  <SortableBatchItem key={batch.id} batch={batch} onDelete={handleDeleteBatch} />
                ))}
              </SortableContext>

              {batches.length === 0 && !isDragOver && (
                <p className="text-gray-400 text-lg text-center py-12">
                  גרור תערובת לכאן או לחץ עליה
                </p>
              )}
            </div>
          </DndContext>

          {batches.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 text-lg text-gray-600 shrink-0">
              סה״כ: {plannedBatches.length + inProgressBatches.length} אצוות בתכנית
              {inProgressBatches.length > 0 && (
                <span className="mr-2 text-yellow-600">({inProgressBatches.length} בביצוע)</span>
              )}
            </div>
          )}
        </div>

        {/* ── Completed Section (leftmost in RTL) ── */}
        <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col overflow-hidden">
          <h2 className="text-2xl font-bold text-green-700 mb-4 shrink-0">✓ הושלמו</h2>
          {doneBatches.length === 0 ? (
            <p className="text-gray-400 text-lg text-center py-6">אין אצוות שהושלמו להיום</p>
          ) : (
            <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
              {doneBatches.map((batch) => (
                <CompletedBatchItem
                  key={batch.id}
                  batch={batch}
                  onBatchUpdated={(updated) => setBatches(prev => prev.map(b => b.id === updated.id ? updated : b))}
                />
              ))}
            </div>
          )}
          {doneBatches.length > 0 && (
            <div className="mt-3 pt-3 border-t border-green-100 text-base text-green-700 font-medium shrink-0">
              סה״כ הושלמו: {doneBatches.length} אצוות
              {doneBatches.some(b => isUnknown(b)) && (
                <span className="mr-2 text-orange-600">(כולל אצוות ללא תערובת)</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Add to Plan Modal ── */}
      {selectedBlend && (
        <AddToPlanModal
          blend={selectedBlend}
          onClose={() => setSelectedBlend(null)}
          onAdd={handleAddBatch}
        />
      )}

      {/* ── Blend Form Modal ── */}
      {showBlendForm && (
        <BlendModal
          blend={editingBlend}
          onClose={() => { setShowBlendForm(false); setEditingBlend(null); }}
          onSave={handleSaveBlend}
        />
      )}
    </div>
  );
}

export default Manager;
